import {type ReactNode} from 'react';
import {cn} from '@chekinapp/ui';

export type PageLayoutHeaderProps = {
  kicker?: ReactNode;
  title?: ReactNode;
  intro?: ReactNode;
  titleAddon?: ReactNode;
  rightAddon?: ReactNode;
  titleAction?: ReactNode;
  hideKickerOnMobile?: boolean;
  hideKickerOnDesktop?: boolean;
  align?: 'start' | 'center';
  size?: 'default' | 'hero';
  kickerTone?: 'brand' | 'muted';
  className?: string;
  children?: ReactNode;
};

const TITLE_SIZE = {
  default: 'text-[23px] leading-[28px] tracking-[-0.015em]',
  hero: 'text-[28px] leading-[34px] tracking-[-0.02em] lg:tracking-[-0.015em]',
} as const;

const KICKER_SIZE = {
  default: 'text-[11px]',
  hero: 'text-[14px]',
} as const;

const KICKER_TONE = {
  brand: 'text-[var(--chekin-color-brand-blue)]',
  muted: 'text-[var(--chekin-text-secondary)] lg:font-bold lg:tracking-[0.16em]',
} as const;

export function PageLayoutHeader({
  kicker,
  title,
  intro,
  titleAddon,
  rightAddon,
  titleAction,
  hideKickerOnMobile,
  hideKickerOnDesktop,
  align = 'start',
  size = 'default',
  kickerTone = 'brand',
  className,
  children,
}: PageLayoutHeaderProps) {
  if (
    !kicker &&
    !title &&
    !intro &&
    !titleAddon &&
    !rightAddon &&
    !titleAction &&
    !children
  )
    return null;

  const isAddonInTitleRow = Boolean(rightAddon) && !kicker;

  return (
    <header
      className={cn(
        'flex flex-col gap-1.5',
        align === 'center' ? 'items-center text-center' : 'items-start text-left',
        className,
      )}
    >
      {(kicker || (rightAddon && !isAddonInTitleRow)) && (
        <div
          className={cn(
            'flex w-full items-center gap-4',
            align === 'center' && 'justify-center',
          )}
        >
          {kicker && (
            <span
              className={cn(
                'min-w-0 font-semibold uppercase tracking-[0.08em]',
                KICKER_SIZE[size],
                KICKER_TONE[kickerTone],
                hideKickerOnMobile && 'hidden lg:inline',
                hideKickerOnDesktop && 'lg:hidden',
              )}
            >
              {kicker}
            </span>
          )}
          {rightAddon && <div className="ml-auto shrink-0">{rightAddon}</div>}
        </div>
      )}
      {(title || isAddonInTitleRow || titleAction) && (
        <div
          className={cn(
            'flex w-full items-center gap-4',
            align === 'center' && 'justify-center',
          )}
        >
          {title && (
            <h1
              className={cn(
                'm-0 min-w-0 font-semibold text-[var(--chekin-color-brand-navy)] lg:text-[27px] lg:leading-[32px]',
                TITLE_SIZE[size],
              )}
            >
              {title}
            </h1>
          )}
          {isAddonInTitleRow && <div className="ml-auto shrink-0">{rightAddon}</div>}
          {titleAction && (
            <div className="ml-auto flex shrink-0 self-start pt-1 lg:pt-1.5">
              {titleAction}
            </div>
          )}
        </div>
      )}
      {titleAddon}
      {intro && (
        <p className="m-0 max-w-[480px] text-sm font-medium leading-[21px] text-[var(--chekin-color-gray-1)]">
          {intro}
        </p>
      )}
      {children}
    </header>
  );
}
